import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, Button, FormHelperText, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { S3Handler } from '../../../utils/s3';
import { useLocales } from '../../../locales';

// ----------------------------------------------------------------------

const MAX_BYTES = 5 * 1024 * 1024;

RHFPdfUpload.propTypes = {
  name: PropTypes.string.isRequired,
  filenameField: PropTypes.string,
  helperText: PropTypes.node,
};

export default function RHFPdfUpload({ name, filenameField, helperText }) {
  const { control, setValue, watch } = useFormContext();
  const { translate } = useLocales();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const url = watch(name);
  const filename = filenameField ? watch(filenameField) : null;

  const handleFile = async (file) => {
    setUploadError(null);
    if (!file) return;
    if (file.type !== 'application/pdf') { setUploadError(translate('dashboard.events.terms.pdf_only')); return; }
    if (file.size > MAX_BYTES) { setUploadError(translate('dashboard.events.terms.max_size')); return; }
    try {
      setIsUploading(true);
      const finalUrl = await S3Handler.uploadFile(file, 'application/pdf', file.name);
      setValue(name, finalUrl, { shouldDirty: true, shouldValidate: true });
      if (filenameField) setValue(filenameField, file.name, { shouldDirty: true });
    } catch (e) {
      setUploadError(e.message || translate('dashboard.events.terms.upload_failed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setValue(name, undefined, { shouldDirty: true, shouldValidate: true });
    if (filenameField) setValue(filenameField, undefined, { shouldDirty: true });
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ fieldState }) => (
        <Stack spacing={1}>
          {!url && (
            <Button
              variant="outlined"
              component="label"
              disabled={isUploading}
              startIcon={isUploading ? null : <span>📄</span>}
            >
              {isUploading ? '…' : translate('dashboard.events.terms.upload_cta')}
              <input
                type="file"
                accept="application/pdf"
                hidden
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }}
              />
            </Button>
          )}
          {url && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ flex: 1 }}>📄 {filename || translate('dashboard.events.terms.default_filename')}</Typography>
              <Button size="small" component="label">
                {translate('dashboard.events.terms.replace')}
                <input type="file" accept="application/pdf" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }} />
              </Button>
              <Button size="small" color="error" onClick={handleRemove}>
                {translate('dashboard.events.terms.remove')}
              </Button>
            </Box>
          )}
          {(uploadError || fieldState.error || helperText) && (
            <FormHelperText error={!!(uploadError || fieldState.error)}>
              {uploadError || fieldState.error?.message || helperText}
            </FormHelperText>
          )}
        </Stack>
      )}
    />
  );
}
